// /rapportvakt/app — the product surface.
//
// Two slim tabs, each with its own URL: /rapportvakt/app/utskick and
// /bevakningar. "Utskick" (Digest) shows every watch the reader has, one card
// each, and the example deliveries if they have none yet. "Bevakningar" is a
// single-column searchable catalogue: the free plan locks every row past the
// first pick, a paid plan locks every row past its own limit (or none at all,
// on the unlimited tier). "Din plan" is a third route, /rapportvakt/app/plan,
// reached from the account control in the top bar (or the footer) rather than
// from a tab: it is account admin, not a place you work. Sign-in is a real
// magic-link session now (docs/plan/2026-09-11-magic-link-login.md) owned by
// Shell.jsx's account context — this page only reads it (useRvAccount)
// rather than owning identity itself.
//
// /rapportvakt/app itself renders the first tab rather than redirecting: it is
// the indexed URL of the app and the one every tab canonicalises to, so it has
// to stay a real page.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import RapportvaktShell, { useRvAccount, useRvLang } from '../components/rapportvakt/Shell';
import { Delivery, Glyph, PublisherMark } from '../components/rapportvakt/Delivery';
import Sheet, { SheetClose } from '../components/rapportvakt/Sheet';
import { loadEmail, loadWatches, saveWatches } from '../components/rapportvakt/storage';
import {
  createOwnFeed,
  createSeriesRequest,
  createSubscription,
  deleteSubscription,
  fetchLatestEdition,
  fetchSubscriptions,
  sendEditionToMe,
} from '../components/rapportvakt/api';
import { editionToDelivery } from '../components/rapportvakt/editionDelivery';
import { track } from '../lib/analytics';
import {
  OWN_FEED_FREQUENCIES,
  ownFeedFrequencyLabel,
  publisherInitials,
} from '../components/rapportvakt/strings';
import {
  STRIPE_CHECKOUT_URLS,
  RAPPORTVAKT_APP_PATH,
  RAPPORTVAKT_APP_TABS,
  RAPPORTVAKT_DELIVERIES_SORTED,
  RAPPORTVAKT_HELP_PATH,
  RAPPORTVAKT_KINDS,
  RAPPORTVAKT_UPCOMING_KINDS,
  RAPPORTVAKT_SERIES,
  RAPPORTVAKT_TIERS,
  getPublisherByName,
  publisherLogo,
  rapportvaktPublisherPath,
  rapportvaktTabFromSegment,
  rapportvaktTabPath,
} from '../config/rapportvakt';
import { getRapportvaktAppSeo } from '../config/seoRoutes';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// GET /subscriptions returns owned feeds in the same list as the catalogue
// pick, marked by this shared publisher name (T6.1). Used to split the two
// apart rather than let an owned feed's slug win the single free "pick".
const OWN_FEED_PUBLISHER_NAME = 'Egen källa';

/** Raw subscription row -> the shape a source row / OwnFeedForm work with. */
function toOwnFeed(row) {
  return {
    slug: row.series_slug,
    name: row.name || row.series_name || row.series_slug,
    frequency: row.frequency,
  };
}

/* --- source kind: the small glyph every row and the add-source preview show --- */

// Merged with RAPPORTVAKT_UPCOMING_KINDS so the add-source preview (C4) can
// show a coming-soon kind's own glyph (e.g. 'x', 'linkedin') rather than
// falling back to 'page'.
const KIND_GLYPH_BY_ID = Object.fromEntries(
  [...RAPPORTVAKT_KINDS, ...RAPPORTVAKT_UPCOMING_KINDS].map((k) => [k.id, k.glyph])
);

/** The 20px kind glyph, in its own chip — same skin as PublisherMark's. */
function KindGlyphMark({ kind, size = 20 }) {
  return (
    <span className="rv-chip rv-chip--kind" aria-hidden="true">
      <Glyph name={KIND_GLYPH_BY_ID[kind] || 'page'} size={size} />
    </span>
  );
}

/**
 * A source row's leading mark (C1): the publisher logo when there is one,
 * otherwise the kind glyph. Never both, a logo already says what it is.
 */
function SourceMark({ kind, publisher }) {
  const hasLogo = Boolean(publisher && publisherLogo(publisher));
  if (hasLogo) return <PublisherMark publisher={publisher} />;
  return <KindGlyphMark kind={kind} />;
}

/* --- add-source kind guess (C4), frontend only ----------------------------
 *
 * A client-side hint only: the real kind is whatever the server resolves the
 * pasted address to. Hosts in UNSUPPORTED_SOURCE_HOSTS reflect what the
 * backend can actually read today (docs/plan/2026-09-13-follow-anything-
 * plan.md, "Supported sources, v1") — the plan adds those kinds later, this
 * list is not permanent.
 */
const UNSUPPORTED_SOURCE_HOSTS = [
  'linkedin.com',
  'x.com',
  'twitter.com',
  'instagram.com',
  'tiktok.com',
  'facebook.com',
];

function hostnameOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
}

function hostMatches(host, domain) {
  return host === domain || host.endsWith(`.${domain}`);
}

function isUnsupportedSourceHost(host) {
  return UNSUPPORTED_SOURCE_HOSTS.some((domain) => hostMatches(host, domain));
}

function guessSourceKind(host) {
  if (host === 'youtu.be' || hostMatches(host, 'youtube.com')) return 'video';
  if (hostMatches(host, 'reddit.com')) return 'social';
  // x.com/twitter.com and linkedin.com are UNSUPPORTED_SOURCE_HOSTS today,
  // but they are also RAPPORTVAKT_UPCOMING_KINDS, so the guess names that
  // kind specifically rather than the generic 'social' — the add-source
  // preview then shows the coming-soon glyph and the upcomingKind message.
  if (host === 'x.com' || host === 'twitter.com') return 'x';
  if (hostMatches(host, 'linkedin.com')) return 'linkedin';
  if (hostMatches(host, 'substack.com')) return 'newsletter';
  return 'page';
}

/** True below the split-layout breakpoint; the digest becomes an accordion. */
function useIsNarrow() {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 960);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

/* --- sheets --------------------------------------------------------------- */

function UpgradeSheet({ series, onClose }) {
  const { t } = useRvLang();
  const name = series ? `${series.report}${t.fromJoiner}${series.publisher}` : null;
  return (
    <Sheet labelledBy="rv-sheet-title" onClose={onClose}>
      <div className="rv-sheet__top">
        <div>
          <h2 className="rv-sheet__title" id="rv-sheet-title">
            {t.unlockTitle(series ? series.report : null)}
          </h2>
          <p className="rv-sheet__sub">{t.unlockBody(name)}</p>
        </div>
        <SheetClose label={t.close} onClose={onClose} />
      </div>

      <TierCards />

      {/* No address in a payment sheet: a raw mailto here reads as the only
          support there is, and it is one click away from a Stripe checkout.
          The question goes to the page that answers it instead. */}
      <p className="rv-sheet__foot">
        {t.sheetFoot}
        <Link to={RAPPORTVAKT_HELP_PATH} onClick={onClose}>
          {t.helpPageLink}
        </Link>
      </p>
    </Sheet>
  );
}

/**
 * Giving a watch back. Destructive and one click away from the watched row,
 * so it asks first and names the series it is about to stop sending.
 * `isFreePlan` decides removeBody's wording: only the free plan's one slot
 * reopens as "a new free watch right away" the moment this one goes.
 */
function RemoveSheet({ series, busy, isFreePlan, onConfirm, onClose }) {
  const { t } = useRvLang();
  // An own feed has no publisher to name — it names itself.
  const name = series
    ? series.publisher
      ? `${series.report}${t.fromJoiner}${series.publisher}`
      : series.report
    : '';

  return (
    <Sheet labelledBy="rv-remove-title" onClose={onClose} className="rv-sheet__panel--narrow">
      <div className="rv-sheet__top">
        <div>
          <h2 className="rv-sheet__title" id="rv-remove-title">
            {t.removeTitle}
          </h2>
          <p className="rv-sheet__sub">{t.removeBody(name, isFreePlan)}</p>
        </div>
        <SheetClose label={t.close} onClose={onClose} />
      </div>

      <div className="rv-sheet__actions">
        <button
          type="button"
          className="rv-btn rv-btn--danger"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? `${t.removing}…` : t.remove}
        </button>
        <button type="button" className="rv-btn rv-btn--ghost" onClick={onClose} disabled={busy}>
          {t.cancel}
        </button>
      </div>
    </Sheet>
  );
}

/**
 * The plan tab's pricing: one table, three rows, with the free tier as the
 * first row instead of a status card of its own. Three fixed columns keep the
 * prices and the actions on shared edges. `currentTier` marks whichever row
 * is the reader's actual plan with "Din plan i dag" and hides that row's
 * Stripe button — the other two rows keep theirs, since either is still an
 * upgrade (or a lateral move) from where the reader is.
 */
function PlanTable({ currentTier }) {
  const { t, lang } = useRvLang();
  return (
    <div className="rv-plantable">
      <div
        className={`rv-plantable__row${currentTier === 'free' ? ' rv-plantable__row--current' : ''}`}
      >
        <div className="rv-plantable__what">
          <span className="rv-plantable__name">{t.planGratisName}</span>
          <span className="rv-plantable__limit">{t.planGratisLimit}</span>
        </div>
        <div className="rv-plantable__price">
          <span className="rv-plantable__amount">0 kr</span>
          <span className="rv-plantable__period">{t.planPeriod}</span>
        </div>
        {currentTier === 'free' ? (
          <span className="rv-plantable__tag">{t.planCurrentTag}</span>
        ) : null}
      </div>
      {RAPPORTVAKT_TIERS.map((tier) => {
        const isCurrent = tier.id === currentTier;
        return (
          <div
            className={`rv-plantable__row${isCurrent ? ' rv-plantable__row--current' : ''}`}
            key={tier.id}
          >
            <div className="rv-plantable__what">
              <span className="rv-plantable__name">{lang === 'en' ? tier.nameEn : tier.name}</span>
              <span className="rv-plantable__limit">{lang === 'en' ? tier.limitEn : tier.limit}</span>
            </div>
            <div className="rv-plantable__price">
              <span className="rv-plantable__amount">{tier.price}</span>
              <span className="rv-plantable__period">{lang === 'en' ? tier.periodEn : tier.period}</span>
            </div>
            {isCurrent ? (
              <span className="rv-plantable__tag">{t.planCurrentTag}</span>
            ) : (
              <a
                className={`rv-btn ${tier.featured ? 'rv-btn--primary' : 'rv-btn--ghost'}`}
                href={STRIPE_CHECKOUT_URLS[tier.id === 'fler' ? 'fler' : 'obegransat']}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('plan_click', { tier: tier.id })}
              >
                {t.chooseTier(lang === 'en' ? tier.nameEn : tier.name)}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TierCards() {
  const { t, lang } = useRvLang();
  return (
    <div className="rv-tiers">
      {RAPPORTVAKT_TIERS.map((tier) => (
        <div className={`rv-tier${tier.featured ? ' rv-tier--featured' : ''}`} key={tier.id}>
          <h3 className="rv-tier__name">{lang === 'en' ? tier.nameEn : tier.name}</h3>
          <div className="rv-tier__price">
            <span className="rv-tier__amount">{tier.price}</span>
            <span className="rv-tier__period">{lang === 'en' ? tier.periodEn : tier.period}</span>
          </div>
          <span className="rv-tier__limit">{lang === 'en' ? tier.limitEn : tier.limit}</span>
          <a
            className={`rv-btn ${tier.featured ? 'rv-btn--primary' : 'rv-btn--ghost'}`}
            href={STRIPE_CHECKOUT_URLS[tier.id === 'fler' ? 'fler' : 'obegransat']}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('plan_click', { tier: tier.id })}
          >
            {t.chooseTier(lang === 'en' ? tier.nameEn : tier.name)}
          </a>
        </div>
      ))}
    </div>
  );
}

/* --- one row in the source list (catalogue pick or own source), C1 -------- */

/**
 * A normalised catalogue series, ready for <SourceRow>.
 *
 * `series` is kept on the result so the row's pick/remove handlers — which
 * take a whole series, not just its id (see choosePick/confirmRemove) — need
 * no lookup back into RAPPORTVAKT_SERIES.
 */
function normalizeCatalogueSource(series) {
  return {
    id: series.id,
    kind: series.kind,
    report: series.report,
    publisher: series.publisher,
    cadence: series.cadence,
    isOwn: false,
    series,
  };
}

/** A subscriber's own source, ready for <SourceRow> — always kind 'page'. */
function normalizeOwnSource(feed, ownFeedFrequencyLabel) {
  return {
    id: feed.slug,
    kind: 'page',
    report: feed.name,
    publisher: null,
    cadence: ownFeedFrequencyLabel(feed.frequency),
    isOwn: true,
    ownFeed: feed,
  };
}

/**
 * One row in the source list: a catalogue pick or a subscriber's own source,
 * both through this one component (C1) rather than two differently-skinned
 * rows. An own source is always "followed" — it exists because the reader
 * added it — so it skips the pick/lock machinery entirely and only ever
 * shows a remove control.
 */
function SourceRow({
  source,
  watches,
  plan,
  isWatched,
  signedIn,
  knownEmail,
  confirmedSeriesId,
  busy,
  ownFeedBusy,
  onPick,
  onLocked,
  onRemove,
  onRemoveOwn,
}) {
  const { t } = useRvLang();
  const [formOpen, setFormOpen] = useState(false);
  const [email, setEmail] = useState(knownEmail);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  // Set once a login link has actually been mailed for this row (signed-out
  // pick only); shows the "check your mail" line in place of the form.
  const [sentEmail, setSentEmail] = useState('');

  const isOwn = source.isOwn;
  const series = isOwn ? null : source.series;
  // Locked once the plan's ceiling is reached — never for a row already
  // watched, never for an own source (nothing to lock, it is already
  // followed), and never at all on the unlimited tier (limit === null).
  const locked = !isOwn && plan.limit !== null && watches.length >= plan.limit && !isWatched;
  const publisher = !isOwn && source.publisher ? getPublisherByName(source.publisher) : null;
  // "Välj gratis" only while this would be the reader's first source on the
  // free plan; every pick after that — a second-and-later source, or any
  // pick at all on a paid plan — is "Följ" instead, since nothing about it
  // is free.
  const pickLabel = watches.length === 0 && plan.tier === 'free' ? t.pickFree : t.watchAction;
  const watchEntry = !isOwn ? watches.find((w) => w.seriesId === source.id) : null;

  // The address arrives from the reconciliation a tick after the first render.
  useEffect(() => {
    if (knownEmail) setEmail((current) => current || knownEmail);
  }, [knownEmail]);

  // Signed in: one click subscribes straight away, no email to collect — the
  // session already says who this is.
  const subscribeNow = async () => {
    if (pending || !series) return;
    setError('');
    setPending(true);
    const result = await onPick(series, null);
    setPending(false);
    if (!result.ok) setError(result.message || t.offlineNote);
  };

  // Signed out: the form asks for an address and requests a login link that
  // also activates this pick (the intent) once the reader clicks it. Until
  // the server answers the form stays open and disabled, and a refusal (a
  // bad email, or the series/frequency the intent named) is shown in the
  // form's own error slot in the server's words.
  const submit = async (event) => {
    event.preventDefault();
    if (pending || !series) return;
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setError(t.loginError);
      return;
    }
    setError('');
    setPending(true);
    const result = await onPick(series, value);
    setPending(false);
    if (!result.ok) {
      setError(result.message || t.offlineNote);
      return;
    }
    setFormOpen(false);
    setSentEmail(value);
  };

  return (
    <div
      className={`rv-row${locked ? ' rv-row--locked' : ''}${isWatched || isOwn ? ' rv-row--picked' : ''}${isOwn ? ' rv-row--own' : ''}`}
    >
      <div className="rv-row__top">
        <SourceMark kind={source.kind} publisher={source.publisher} />
        <div className="rv-row__text">
          <div className="rv-row__report">{source.report}</div>
          <div className="rv-row__meta">
            {isOwn ? (
              `${t.ownFeedTag}, ${source.cadence}`
            ) : (
              <>
                {/* The publisher name is the way out of the catalogue and into
                    the explorative layer, for a reader who wants to know who
                    this is before spending a pick on them. */}
                {publisher ? (
                  <Link className="rv-row__pub" to={rapportvaktPublisherPath(publisher.slug)}>
                    {source.publisher}
                  </Link>
                ) : (
                  source.publisher
                )}
                . {source.cadence}
              </>
            )}
          </div>
        </div>
        {isOwn ? (
          <span className="rv-row__actions">
            <button
              type="button"
              className="rv-row__action rv-row__action--remove"
              onClick={() => onRemoveOwn(source.ownFeed)}
              disabled={ownFeedBusy}
              aria-label={`${t.removeWatch}: ${source.report}`}
            >
              <Glyph name="cross" size={12} />
              {ownFeedBusy ? `${t.removing}…` : t.remove}
            </button>
          </span>
        ) : isWatched ? (
          <span className="rv-row__actions">
            <span className="rv-row__action rv-row__action--picked">
              <Glyph name="check" size={12} />
              {t.yourWatch}
            </span>
            <button
              type="button"
              className="rv-row__action rv-row__action--remove"
              onClick={() => onRemove(series)}
              disabled={busy}
              aria-label={`${t.removeWatch}: ${series.report}`}
            >
              <Glyph name="cross" size={12} />
              {busy ? `${t.removing}…` : t.remove}
            </button>
          </span>
        ) : locked ? (
          <button type="button" className="rv-row__action" onClick={() => onLocked(series)}>
            <Glyph name="lock" size={12} />
            {t.unlock}
          </button>
        ) : (
          <button
            type="button"
            className="rv-row__action rv-row__action--free"
            onClick={() => {
              track('follow_click', { kind: 'catalogue' });
              return signedIn ? subscribeNow() : setFormOpen((open) => !open);
            }}
            aria-expanded={signedIn ? undefined : formOpen}
            disabled={signedIn && pending}
          >
            <Glyph name="plus" size={12} />
            {signedIn && pending ? `${t.activating}…` : pickLabel}
          </button>
        )}
      </div>

      {!isOwn && isWatched && confirmedSeriesId === source.id ? (
        <p className="rv-row__done">
          <Glyph name="check" size={12} />
          {t.pickDone(series.report, series.publisher, watchEntry ? watchEntry.email : email)}
        </p>
      ) : null}

      {!isOwn && !isWatched && signedIn && error ? <p className="rv-pickform__error">{error}</p> : null}

      {!isOwn && !isWatched && !signedIn && sentEmail ? (
        <p className="rv-row__done">
          <Glyph name="check" size={12} />
          {t.pickCheckMail(sentEmail)}
        </p>
      ) : null}

      {!isOwn && formOpen && !isWatched && !locked && !signedIn ? (
        <form className="rv-pickform" onSubmit={submit}>
          <div className="rv-pickform__row">
            <input
              className="rv-input"
              id={`rv-email-${source.id}`}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t.loginPlaceholder}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={pending}
              aria-label={t.emailForLabel(source.report)}
            />
            <button type="submit" className="rv-btn rv-btn--primary" disabled={pending}>
              {pending ? `${t.activating}…` : t.activate}
            </button>
          </div>
          {error ? <p className="rv-pickform__error">{error}</p> : null}
        </form>
      ) : null}
    </div>
  );
}

/**
 * One watch's latest edition, on the Utskick tab: a small "Senaste
 * utskicket" line naming the watch and its date, the edition itself
 * rendered through the same <Delivery> the sample and public digest pages
 * use (so visuals, quotes and page links all work exactly the same way),
 * a "Skicka till mig" button, and — last, smaller — the same "din bevakning
 * är aktiv" line the plain watchcard below shows. The line only has to say
 * when the *next* one lands now; what the reader got already sits above it.
 *
 * `entry` is one row of the unified digest list RapportvaktAppBody builds
 * (a catalogue watch or an own feed, normalised to { slug, name, cadence }
 * either way); `edition` is that series' latest EditionOut from the server.
 */
function LatestDeliverySection({ entry, edition }) {
  const { t } = useRvLang();
  const delivery = useMemo(() => editionToDelivery(edition), [edition]);
  // { pending, message }: message is either the "skickat till ..." line or a
  // refusal/offline note, in the server's own words where it has one.
  const [sendState, setSendState] = useState({ pending: false, message: '' });

  const send = async () => {
    if (sendState.pending) return;
    setSendState({ pending: true, message: '' });
    const result = await sendEditionToMe(edition.slug);
    setSendState(
      result.ok
        ? { pending: false, message: t.sentTo(result.email) }
        : { pending: false, message: result.message || t.offlineNote }
    );
  };

  return (
    <section className="rv-latest" aria-labelledby={`rv-latest-head-${entry.slug}`}>
      <p className="rv-latest__head" id={`rv-latest-head-${entry.slug}`}>
        {t.latestDelivery}: {entry.name}, {delivery.dateLabel}
      </p>
      <Delivery delivery={delivery} />
      <div className="rv-latest__actions">
        <button
          type="button"
          className="rv-btn rv-btn--ghost"
          onClick={send}
          disabled={sendState.pending}
        >
          {sendState.pending ? `${t.sending}…` : t.sendToMe}
        </button>
        {sendState.message ? <p className="rv-latest__sent">{sendState.message}</p> : null}
      </div>
      <p className="rv-latest__next">{t.watchActive(entry.cadence)}</p>
    </section>
  );
}

/**
 * "Bevaka en webbsida": URL + frequency, calling POST /series for the
 * signed-in subscriber (the session says who this is — no address to
 * collect here any more; see OwnFeedLoginForm below for the signed-out
 * sheet, which asks for one to send a login link instead).
 *
 * When the server cannot turn the address into a watch (400 — no page found
 * there), the form falls back to a request it can send by hand instead: the
 * RequestPanel below opens automatically, pre-filled with the address that
 * failed. The same panel is reachable any time via the standalone "missing a
 * publisher?" link under the hint, in which case the url comes from whatever
 * is currently typed (possibly nothing) and a note becomes mandatory.
 *
 * Lives inside the OwnFeedSheet now. `onAdded` fires only on a successful
 * add (closing the sheet); a request sent via RequestPanel leaves the sheet
 * open so the reader reads the "Tack!" line. `urlInputRef` is handed to
 * Sheet as its initial-focus target, so the URL field is focused on open.
 */
function OwnFeedForm({ accountEmail, onAdd, onAdded, urlInputRef }) {
  const { t, lang } = useRvLang();
  const [feedUrl, setFeedUrl] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  // null while the request panel is hidden. `url` is a snapshot taken the
  // moment the panel opens, so it does not drift if the reader keeps editing
  // the address field underneath.
  const [request, setRequest] = useState(null);
  const [requestNote, setRequestNote] = useState('');
  const [requestPending, setRequestPending] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestDone, setRequestDone] = useState(false);

  const openRequest = (url) => {
    setRequest({ url });
    setRequestNote('');
    setRequestError('');
    setRequestDone(false);
  };

  // The client-side preview (C4): a kind glyph and the hostname, guessed the
  // moment the address looks like a URL, well before the server has said
  // anything. `unsupported` names a host we already know the backend cannot
  // read yet (see UNSUPPORTED_SOURCE_HOSTS above) — submit stays disabled
  // rather than let the reader wait for a refusal we can already predict.
  const host = hostnameOf(feedUrl.trim());
  const guessedKind = host ? guessSourceKind(host) : null;
  const unsupported = host ? isUnsupportedSourceHost(host) : false;
  // x.com, twitter.com and linkedin.com are unsupported today but also
  // RAPPORTVAKT_UPCOMING_KINDS entries (see guessSourceKind above) — those
  // three get the upcomingKind message instead of the plain "not supported"
  // one. instagram.com, tiktok.com and facebook.com have no upcoming kind
  // yet, so they keep sourceNotSupported.
  const upcomingKindEntry = unsupported
    ? RAPPORTVAKT_UPCOMING_KINDS.find((k) => k.id === guessedKind)
    : null;

  const submit = async (event) => {
    event.preventDefault();
    track('follow_click', { kind: guessedKind || 'own' });
    if (pending || unsupported) return;
    const urlValue = feedUrl.trim();
    if (!urlValue) {
      setError(t.ownFeedEmptyUrl);
      return;
    }
    setError('');
    setPending(true);
    const result = await onAdd(urlValue, frequency);
    setPending(false);
    track('source_added', { ok: result.ok });
    if (result.ok) {
      setFeedUrl('');
      setRequest(null);
      if (onAdded) onAdded();
      return;
    }
    setError(result.message || t.offlineNote);
    // A plain "found nothing there" refusal offers the hand-added fallback
    // right away, with the address that just failed already attached.
    if (result.status === 400) openRequest(urlValue);
  };

  const submitRequest = async () => {
    if (requestPending || !request) return;
    const noteValue = requestNote.trim();
    if (!request.url && !noteValue) {
      setRequestError(t.requestNeedNote);
      return;
    }
    setRequestError('');
    setRequestPending(true);
    const result = await createSeriesRequest(accountEmail, request.url, noteValue);
    setRequestPending(false);
    if (result.ok) {
      setRequestDone(true);
      return;
    }
    setRequestError(result.message || t.offlineNote);
  };

  return (
    <form className="rv-ownfeed" onSubmit={submit}>
      <div className="rv-pickform__row">
        <input
          className="rv-input"
          ref={urlInputRef}
          type="url"
          inputMode="url"
          placeholder={t.ownFeedUrlPlaceholder}
          value={feedUrl}
          onChange={(event) => setFeedUrl(event.target.value)}
          disabled={pending}
          aria-label={t.ownFeedUrlLabel}
        />
      </div>

      {/* Preview card (C4): shown as soon as the pasted address resolves to a
          host, before the reader ever submits. */}
      {host ? (
        <div className="rv-source-preview">
          <KindGlyphMark kind={guessedKind} />
          <span className="rv-source-preview__name">{host}</span>
        </div>
      ) : null}

      {unsupported ? (
        <p className="rv-pickform__error">
          {upcomingKindEntry
            ? t.upcomingKind(lang === 'en' ? upcomingKindEntry.labelEn : upcomingKindEntry.label)
            : t.sourceNotSupported}
        </p>
      ) : (
        <div className="rv-ownfeed__freq" role="group" aria-label={t.ownFeedFreqLabel}>
          {OWN_FEED_FREQUENCIES.map((f) => (
            <button
              type="button"
              className="rv-filter"
              key={f.id}
              aria-pressed={frequency === f.id}
              onClick={() => setFrequency(f.id)}
              disabled={pending}
            >
              {lang === 'en' ? f.labelEn : f.label}
            </button>
          ))}
        </div>
      )}
      <div className="rv-pickform__row">
        <button type="submit" className="rv-btn rv-btn--primary" disabled={pending || unsupported}>
          {pending ? t.ownFeedPending : t.ownFeedSubmit}
        </button>
      </div>
      <p className="rv-pickform__hint">{t.ownFeedHint}</p>
      {error ? <p className="rv-pickform__error">{error}</p> : null}
      {request ? (
        <RequestPanel
          note={requestNote}
          onNoteChange={setRequestNote}
          onSubmit={submitRequest}
          pending={requestPending}
          error={requestError}
          done={requestDone}
        />
      ) : (
        <button type="button" className="rv-linkbtn rv-ownfeed__requestOpen" onClick={() => openRequest(feedUrl.trim())}>
          {t.requestOpen}
        </button>
      )}
    </form>
  );
}

/**
 * The own-feed sheet, signed out: there is nothing to add yet — a page is
 * only ever added for the signed-in subscriber's own session — so this is
 * one sentence saying why, plus the same login-link form the top bar's
 * popover offers, without an intent (a login link cannot resume "add this
 * feed" the way a catalogue pick's intent can, since the reader has not
 * typed the address yet when they land back on /logga-in).
 */
function OwnFeedLoginForm({ knownEmail, emailInputRef }) {
  const { t } = useRvLang();
  const { requestLink } = useRvAccount();
  const [email, setEmail] = useState(knownEmail);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    if (pending) return;
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setError(t.loginError);
      return;
    }
    setError('');
    setPending(true);
    const result = await requestLink(value);
    setPending(false);
    if (!result.ok) {
      setError(result.message || t.offlineNote);
      return;
    }
    track('login_link_sent');
    setSentEmail(value);
  };

  if (sentEmail) {
    return <p className="rv-pickform__hint">{t.loginSent(sentEmail)}</p>;
  }

  return (
    <>
      <p className="rv-sheet__sub">{t.ownFeedLoginNudge}</p>
      <form className="rv-pickform" onSubmit={submit}>
        <div className="rv-pickform__row">
          <input
            className="rv-input"
            ref={emailInputRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t.loginPlaceholder}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setError('');
            }}
            disabled={pending}
            aria-label={t.loginEmailLabel}
          />
          <button type="submit" className="rv-btn rv-btn--primary" disabled={pending}>
            {pending ? `${t.sending}…` : t.loginBtn}
          </button>
        </div>
        {error ? <p className="rv-pickform__error">{error}</p> : null}
      </form>
    </>
  );
}

/**
 * The "add it by hand" fallback for OwnFeedForm: an optional note (required
 * only when the caller opened the panel with no url attached) and a button
 * that posts a series request. Kept as its own component so OwnFeedForm's
 * submit/openRequest wiring stays readable.
 */
function RequestPanel({ note, onNoteChange, onSubmit, pending, error, done }) {
  const { t } = useRvLang();
  if (done) {
    return <p className="rv-ownfeed__thanks">{t.requestThanks}</p>;
  }
  return (
    <div className="rv-ownfeed__request">
      <p className="rv-ownfeed__request-head">{t.requestHead}</p>
      <div className="rv-pickform__row">
        <textarea
          className="rv-input rv-ownfeed__note"
          placeholder={t.requestNotePlaceholder}
          maxLength={2000}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          disabled={pending}
          aria-label={t.requestNoteLabel}
        />
      </div>
      <div className="rv-pickform__row">
        <button type="button" className="rv-btn rv-btn--primary" onClick={onSubmit} disabled={pending}>
          {pending ? t.requestPending : t.requestSubmit}
        </button>
      </div>
      {error ? <p className="rv-pickform__error">{error}</p> : null}
    </div>
  );
}

/**
 * The sheet "Bevaka en webbsida" (the "+" button top right of the
 * Bevakningar tab) opens: OwnFeedForm's title moves up into the sheet's own
 * heading, so the form no longer prints its own. Closes itself on a
 * successful add; stays open through a request-sent "Tack!" so the reader
 * can read it, using the sheet's own close control.
 *
 * Signed out, there is no form to add a page with — see OwnFeedLoginForm's
 * own comment — so the sheet's body is that sentence and a login-link form
 * instead. `knownEmail` is only the storage.js display cache, used to
 * prefill that form; it plays no part once signed in.
 */
function OwnFeedSheet({ knownEmail, onAdd, onClose }) {
  const { t } = useRvLang();
  const { status, email: accountEmail } = useRvAccount();
  const signedIn = status === 'in';
  const urlInputRef = useRef(null);
  const emailInputRef = useRef(null);
  return (
    <Sheet
      labelledBy="rv-ownfeed-title"
      onClose={onClose}
      className="rv-sheet__panel--ownfeed"
      initialFocusRef={signedIn ? urlInputRef : emailInputRef}
    >
      <div className="rv-sheet__top">
        <h2 className="rv-sheet__title" id="rv-ownfeed-title">
          {t.ownFeedHead}
        </h2>
        <SheetClose label={t.close} onClose={onClose} />
      </div>
      {signedIn ? (
        <OwnFeedForm accountEmail={accountEmail} onAdd={onAdd} onAdded={onClose} urlInputRef={urlInputRef} />
      ) : (
        <OwnFeedLoginForm knownEmail={knownEmail} emailInputRef={emailInputRef} />
      )}
    </Sheet>
  );
}

/* --- page ----------------------------------------------------------------- */

/**
 * The frame first, the app inside it. RapportvaktShell owns the account
 * context (see useRvAccount in Shell.jsx), and a context is only visible to
 * what renders *under* its provider — so the state and effects that react to
 * sign-in/sign-out live in RapportvaktApp, a child of the shell, not in this
 * outer component, which would only ever see the context's empty default.
 */
export default function RapportvaktPage() {
  const seo = getRapportvaktAppSeo();
  return (
    <RapportvaktShell seo={seo}>
      <RapportvaktApp />
    </RapportvaktShell>
  );
}

function RapportvaktApp() {
  const { tab: segment } = useParams();
  const tab = rapportvaktTabFromSegment(segment);
  // The account context is the source of truth for identity and plan now
  // (docs/plan/2026-09-11-magic-link-login.md) — this page only reacts to
  // it. `refresh` is called whenever an authed request below comes back
  // 401: the session was already forgotten inside api.js at that point, so
  // this just makes the header notice too, same as a real sign-out.
  const { status, email: accountEmail, tier, limit, requestLink, refresh } = useRvAccount();
  const signedIn = status === 'in';
  const plan = useMemo(() => ({ tier, limit }), [tier, limit]);
  // The last address typed into any sign-in or pick form — a display cache
  // only (storage.js), used to prefill a signed-out reader's forms. Not
  // reactive: it only ever seeds a form's initial value.
  const cachedEmail = loadEmail();
  const [watches, setWatches] = useState([]);
  // The one-free-pick "klart!" line under a row: which series it belongs to,
  // so it only shows on that row, and only until the reader navigates away
  // from the moment they claimed it — it is not restored from storage.
  const [confirmedSeriesId, setConfirmedSeriesId] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [lockedSeries, setLockedSeries] = useState(null);
  const [removingSeriesId, setRemovingSeriesId] = useState(null);
  const [ownFeeds, setOwnFeeds] = useState([]);
  const [removingOwnFeed, setRemovingOwnFeed] = useState(null);
  const [ownFeedRemoveBusy, setOwnFeedRemoveBusy] = useState(false);
  const [ownFeedSheetOpen, setOwnFeedSheetOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(RAPPORTVAKT_DELIVERIES_SORTED[0].id);
  const [openIds, setOpenIds] = useState([RAPPORTVAKT_DELIVERIES_SORTED[0].id]);
  const [query, setQuery] = useState('');
  // The kind filter (C1/C2): replaces the old cadence chips. 'all' shows
  // every source; anything else is a RAPPORTVAKT_KINDS id.
  const [kind, setKind] = useState('all');
  // The reader's own latest edition per watched series, keyed by series
  // slug — a catalogue watch's seriesId and an own feed's slug are both
  // series slugs, so one map covers both. `null` means "asked, nothing sent
  // yet"; a missing key means "not asked (yet)".
  const [latestEditions, setLatestEditions] = useState({});
  // Every series slug the reader watches right now, catalogue picks and own
  // feeds together — both carry a real series slug on the server. Joined
  // into one string so the fetch effect below has a stable dependency: the
  // array itself is a new reference every render, but the string it spells
  // out only changes when the actual set of slugs does.
  const digestSlugs = useMemo(
    () => Array.from(new Set([...watches.map((w) => w.seriesId), ...ownFeeds.map((f) => f.slug)])),
    [watches, ownFeeds]
  );
  const digestSlugsKey = digestSlugs.join(',');

  /**
   * Split a /subscriptions response into own feeds and catalogue watches and
   * paint both from it. The only caller is the effect below, which reruns
   * this on every sign-in. Tier/limit are not read from here any more — the
   * account context is the source of truth for the plan now, so this only
   * ever touches the watch lists.
   */
  const applySubscriptions = useCallback((email, result) => {
    const rows = result.rows || [];
    const ownRows = rows.filter((row) => row && row.publisher_name === OWN_FEED_PUBLISHER_NAME);
    const watchRows = rows.filter((row) => !(row && row.publisher_name === OWN_FEED_PUBLISHER_NAME));
    setOwnFeeds(ownRows.map(toOwnFeed));
    const nextWatches = watchRows
      .filter((row) => row && typeof row.series_slug === 'string')
      .map((row) => ({ seriesId: row.series_slug, email }));
    setWatches(nextWatches);
    saveWatches(nextWatches);
  }, []);

  // Reruns whenever sign-in state changes, not just on mount — sign in and
  // sign out now happen from the top bar, on any page, so this page has to
  // notice the change rather than only ever reading it once.
  // Signed out: nothing to show, and nothing to ask the server either.
  // Signed in: paint the cache first (only the rows that are actually this
  // address's — a stale cache from a previous sign-in should never flash
  // under a new one), then ask the server and let it win.
  useEffect(() => {
    if (!signedIn) {
      setWatches([]);
      setOwnFeeds([]);
      // A fresh sign-in (possibly as someone else) should never inherit
      // another session's fetched editions.
      setLatestEditions({});
      return undefined;
    }

    setWatches(loadWatches().filter((watch) => watch.email === accountEmail));

    let cancelled = false;
    fetchSubscriptions().then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        if (result.status === 401) {
          refresh();
          return;
        }
        // Unreachable: keep the cached rows and say so, quietly.
        setNotice('offline');
        return;
      }
      setNotice('');
      applySubscriptions(accountEmail, result);
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn, accountEmail, applySubscriptions, refresh]);

  // The reader's latest edition per watch, re-fetched only when the set of
  // slugs changes or the reader comes back to Utskick — the effect's deps
  // are that key and the tab, so a plain re-render asks for nothing. Only
  // asked for while the Utskick tab is actually open: the other tabs have no
  // use for it, and a reader who never opens Utskick never pays for it.
  // No "already fetched" ref here on purpose: paired with the cancelled flag
  // it dropped every result under React 18's development StrictMode, which
  // runs the effect, its cleanup and the effect again — the second run saw
  // the ref set and returned, and the first run's answer was already
  // cancelled. A repeated fetch is cheap; a blank tab is not.
  // fetchLatestEdition never rejects and never reports an error (an
  // unreachable server resolves to "no edition yet", same as a series that
  // has genuinely never sent one) — see its own doc comment in api.js.
  useEffect(() => {
    if (tab !== 'digest' || digestSlugsKey === '') return undefined;
    let cancelled = false;
    Promise.all(
      digestSlugs.map((slug) => fetchLatestEdition(slug).then((result) => [slug, result.edition]))
    ).then((entries) => {
      if (cancelled) return;
      setLatestEditions((current) => {
        const next = { ...current };
        entries.forEach(([slug, edition]) => {
          next[slug] = edition;
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // digestSlugs is derived from digestSlugsKey, which is the dep that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, digestSlugsKey]);

  /**
   * Resolves { ok, mode } or { ok: false, message } so the row can show the
   * refusal. Signed in: subscribes at once, the session already says who
   * this is. Signed out: asks for a login link carrying this pick as its
   * intent, so clicking the mailed link both signs the reader in and
   * activates the watch — `mode: 'linkSent'` tells the row to show "Kolla
   * mejlen" instead of the usual "klart!" line, since nothing is watched yet.
   */
  const choosePick = useCallback(
    async (series, pickEmail) => {
      setNotice('');
      if (signedIn) {
        const result = await createSubscription(series.id);
        if (!result.ok) {
          if (result.status === 401) refresh();
          // An empty message means "unreachable"; the row falls back to its
          // own localised note rather than inventing a server message.
          return { ok: false, message: result.offline ? '' : result.message };
        }
        const wasFirstWatch = watches.length === 0;
        setWatches((current) => {
          if (current.some((w) => w.seriesId === series.id)) return current;
          const next = [...current, { seriesId: series.id, email: accountEmail }];
          saveWatches(next);
          return next;
        });
        if (wasFirstWatch) setConfirmedSeriesId(series.id);
        return { ok: true, mode: 'subscribed' };
      }
      const result = await requestLink(pickEmail, { series_slug: series.id });
      if (!result.ok) {
        return { ok: false, message: result.offline ? '' : result.message };
      }
      return { ok: true, mode: 'linkSent' };
    },
    [signedIn, watches, accountEmail, requestLink, refresh]
  );

  const confirmRemove = useCallback(async () => {
    const watch = watches.find((w) => w.seriesId === removingSeriesId);
    if (!watch) {
      setRemovingSeriesId(null);
      return;
    }
    setRemoveBusy(true);
    const result = await deleteSubscription(watch.seriesId);
    setRemoveBusy(false);
    if (!result.ok) {
      if (result.status === 401) refresh();
      setNotice('offline');
      setRemovingSeriesId(null);
      return;
    }
    setNotice('');
    setWatches((current) => {
      const next = current.filter((w) => w.seriesId !== removingSeriesId);
      saveWatches(next);
      return next;
    });
    if (confirmedSeriesId === removingSeriesId) setConfirmedSeriesId(null);
    setRemovingSeriesId(null);
  }, [watches, removingSeriesId, confirmedSeriesId, refresh]);

  /**
   * Resolves { ok } or { ok: false, status, message }, same contract as
   * choosePick, so OwnFeedForm can show the server's refusal (or its own
   * offline note) in place — `status` lets it tell a plain "no page found"
   * (400) apart from every other refusal, to offer the request panel. Only
   * ever called while signed in — see OwnFeedSheet — so there is no email
   * to pass along; the session says who this is.
   */
  const addOwnFeed = useCallback(
    async (feedUrl, frequency) => {
      setNotice('');
      const result = await createOwnFeed(feedUrl, frequency);
      if (!result.ok) {
        if (result.status === 401) refresh();
        return {
          ok: false,
          status: result.offline ? 0 : result.status,
          message: result.offline ? '' : result.message,
        };
      }
      const series = result.series || {};
      const slug = series.slug;
      if (slug) {
        setOwnFeeds((current) => {
          if (current.some((feed) => feed.slug === slug)) return current;
          return [...current, { slug, name: series.name || slug, frequency }];
        });
      }
      return { ok: true };
    },
    [refresh]
  );

  const confirmRemoveOwnFeed = useCallback(async () => {
    if (!removingOwnFeed) return;
    setOwnFeedRemoveBusy(true);
    const result = await deleteSubscription(removingOwnFeed.slug, removingOwnFeed.frequency);
    setOwnFeedRemoveBusy(false);
    if (!result.ok) {
      if (result.status === 401) refresh();
      setNotice('offline');
      setRemovingOwnFeed(null);
      return;
    }
    setNotice('');
    setOwnFeeds((current) => current.filter((feed) => feed.slug !== removingOwnFeed.slug));
    setRemovingOwnFeed(null);
  }, [removingOwnFeed, refresh]);

  const isNarrow = useIsNarrow();
  const selected =
    RAPPORTVAKT_DELIVERIES_SORTED.find((d) => d.id === selectedId) ||
    RAPPORTVAKT_DELIVERIES_SORTED[0];
  // The catalogue series behind whichever row is mid-removal, for the sheet
  // to name (RemoveSheet only knows how to name a { report, publisher }).
  const removingSeries = removingSeriesId
    ? RAPPORTVAKT_SERIES.find((s) => s.id === removingSeriesId)
    : null;

  // Own sources first, catalogue picks after — one list, one row component
  // (SourceRow, C1) — filtered by the search box and the kind chips
  // together. Unfiltered by the plan's pick limit: that only decides which
  // rows are locked, not which rows show up.
  const allSources = useMemo(
    () => [
      ...ownFeeds.map((feed) => normalizeOwnSource(feed, ownFeedFrequencyLabel)),
      ...RAPPORTVAKT_SERIES.map(normalizeCatalogueSource),
    ],
    [ownFeeds]
  );

  // Only the kinds actually present get a chip (C1: "only kinds that have at
  // least one row"), computed from the full list so the chips do not shuffle
  // as the reader types a search query.
  const availableKinds = useMemo(() => {
    const present = new Set(allSources.map((source) => source.kind));
    return RAPPORTVAKT_KINDS.filter((k) => present.has(k.id));
  }, [allSources]);

  const sourceRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allSources.filter((source) => {
      if (kind !== 'all' && source.kind !== kind) return false;
      if (!q) return true;
      const haystack = `${source.report} ${source.publisher || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [allSources, query, kind]);

  // An unknown segment is not a tab, so it is not the app. Send it to the app's
  // own URL rather than rendering a third variant of the same page.
  if (!tab) return <Navigate to={`${RAPPORTVAKT_APP_PATH}/`} replace />;

  return (
    <>
      <RapportvaktAppBody
        tab={tab}
        selected={selected}
        isNarrow={isNarrow}
        openIds={openIds}
        setOpenIds={setOpenIds}
        setSelectedId={setSelectedId}
        watches={watches}
        plan={plan}
        signedIn={signedIn}
        accountEmail={accountEmail}
        cachedEmail={cachedEmail}
        confirmedSeriesId={confirmedSeriesId}
        removeBusy={removeBusy}
        notice={notice}
        choosePick={choosePick}
        setLockedSeries={setLockedSeries}
        setRemovingSeriesId={setRemovingSeriesId}
        sourceRows={sourceRows}
        availableKinds={availableKinds}
        query={query}
        setQuery={setQuery}
        kind={kind}
        setKind={setKind}
        ownFeeds={ownFeeds}
        ownFeedRemoveBusy={ownFeedRemoveBusy}
        setRemovingOwnFeed={setRemovingOwnFeed}
        openOwnFeedSheet={() => setOwnFeedSheetOpen(true)}
        latestEditions={latestEditions}
      />
      {ownFeedSheetOpen ? (
        <OwnFeedSheet
          knownEmail={cachedEmail}
          onAdd={addOwnFeed}
          onClose={() => setOwnFeedSheetOpen(false)}
        />
      ) : null}
      {lockedSeries ? (
        <UpgradeSheet series={lockedSeries} onClose={() => setLockedSeries(null)} />
      ) : null}
      {removingSeries ? (
        <RemoveSheet
          series={removingSeries}
          busy={removeBusy}
          isFreePlan={plan.tier === 'free'}
          onConfirm={confirmRemove}
          onClose={() => setRemovingSeriesId(null)}
        />
      ) : null}
      {removingOwnFeed ? (
        <RemoveSheet
          series={{ report: removingOwnFeed.name, publisher: null }}
          busy={ownFeedRemoveBusy}
          isFreePlan={plan.tier === 'free'}
          onConfirm={confirmRemoveOwnFeed}
          onClose={() => setRemovingOwnFeed(null)}
        />
      ) : null}
    </>
  );
}

function RapportvaktAppBody({
  tab,
  selected,
  isNarrow,
  openIds,
  setOpenIds,
  setSelectedId,
  watches,
  plan,
  signedIn,
  accountEmail,
  cachedEmail,
  confirmedSeriesId,
  removeBusy,
  notice,
  choosePick,
  setLockedSeries,
  setRemovingSeriesId,
  sourceRows,
  availableKinds,
  query,
  setQuery,
  kind,
  setKind,
  ownFeeds,
  ownFeedRemoveBusy,
  setRemovingOwnFeed,
  openOwnFeedSheet,
  latestEditions,
}) {
  const { t, lang } = useRvLang();

  const labels = { digest: t.tabDigest, bevakningar: t.tabWatches };
  // A subscriber's Utskick tab is theirs. The examples are how the product is
  // explained before you sign up, so once there is a real watch (a catalogue
  // pick, or an own feed) they go: the tab then shows a card per watch and
  // nothing on it is sample material a reader has to sort out from the real
  // thing.
  const showSamples = tab === 'digest' && watches.length === 0 && ownFeeds.length === 0;

  // One normalised row per watch, catalogue picks and own feeds together —
  // both are things the reader watches, and the tab's own promise ("every
  // watch, one card each") does not carve own feeds out of that. A
  // catalogue watch whose series id no longer resolves (should not happen,
  // but the old code guarded it too) is dropped rather than rendered blank.
  const digestEntries = useMemo(() => {
    const catalogueEntries = watches
      .map((watch) => {
        const series = RAPPORTVAKT_SERIES.find((s) => s.id === watch.seriesId);
        if (!series) return null;
        return { slug: series.id, name: series.report, publisher: series.publisher, cadence: series.cadence };
      })
      .filter(Boolean);
    const ownEntries = ownFeeds.map((feed) => ({
      slug: feed.slug,
      name: feed.name,
      publisher: null,
      cadence: ownFeedFrequencyLabel(feed.frequency),
    }));
    return [...catalogueEntries, ...ownEntries];
  }, [watches, ownFeeds]);
  // Bevakningar's closing line depends on the plan: the free line names the
  // one-pick rule; a paid tier states its own ceiling (or that there is
  // none) instead, since the free rule no longer applies to it.
  const catalogIntro =
    plan.tier === 'fler'
      ? t.catalogIntroFler
      : plan.tier === 'obegransat'
        ? t.catalogIntroObegransat
        : t.catalogIntro;

  return (
    <main className="rv__inner">
      {/* Links, not buttons: each tab is a URL, so it can be opened in a new
          tab, bookmarked and reloaded. */}
      <nav className="rv-tabs" aria-label="Punktlista">
        {RAPPORTVAKT_APP_TABS.map((item) => (
          <Link
            className="rv-tab"
            key={item.id}
            id={`rv-tab-${item.id}`}
            to={rapportvaktTabPath(item.id)}
            aria-current={tab === item.id ? 'page' : undefined}
          >
            {labels[item.id]}
          </Link>
        ))}
      </nav>

      {/* One card per watch, in the order the server returned them: a real
          latest edition where the reader already has one (rendered through
          the same <Delivery> the sample pages use, with its own "skicka
          till mig" button), otherwise the plain status card with the
          cadence quoted verbatim from the catalogue — it is the only thing
          we honestly know about when the next report lands. Wrapped in the
          same .rv-tabpanel as every other tab's content so the Utskick tab
          merges into it too, whether or not the reader has a real watch
          yet. */}
      {tab === 'digest' && digestEntries.length > 0 ? (
        <div className="rv-tabpanel" id="rv-panel-digest">
          {digestEntries.map((entry) => {
            const edition = latestEditions[entry.slug];
            if (edition) {
              return <LatestDeliverySection entry={entry} edition={edition} key={entry.slug} />;
            }
            return (
              <section
                className="rv-watchcard"
                aria-labelledby={`rv-watchcard-head-${entry.slug}`}
                key={entry.slug}
              >
                {entry.publisher ? (
                  <PublisherMark publisher={entry.publisher} />
                ) : (
                  <span className="rv-chip" aria-hidden="true">
                    {publisherInitials(t.ownFeedTag)}
                  </span>
                )}
                <div className="rv-watchcard__text">
                  <h2 className="rv-watchcard__head" id={`rv-watchcard-head-${entry.slug}`}>
                    {t.watchStatusHead}
                  </h2>
                  <p className="rv-watchcard__name">{entry.name}</p>
                  {entry.publisher ? <p className="rv-watchcard__pub">{entry.publisher}</p> : null}
                  <p className="rv-watchcard__note">{t.watchActive(entry.cadence)}</p>
                </div>
              </section>
            );
          })}

          {/* Only true while at least one watch is still on its plain status
              card: once every watch has a real edition above it, this line
              (about a *first* delivery) no longer describes anything. */}
          {digestEntries.some((entry) => !latestEditions[entry.slug]) ? (
            <p className="rv-firstnote">
              {t.firstDeliveryNote}{' '}
              <Link to={RAPPORTVAKT_HELP_PATH}>{t.firstDeliveryLink}</Link>
            </p>
          ) : null}
        </div>
      ) : null}

      {showSamples ? (
        isNarrow ? (
          /* Accordion: each row expands in place, so the delivery always sits
             directly under the row it belongs to. The label lives inside the
             panel, as its first child, so the tabs above have only the panel
             to merge into — nothing floats between them. */
          <div className="rv-tabpanel rv-board rv-accordion" id="rv-panel-digest">
            <div className="rv-samples">
              <h2 className="rv-samples__head">{t.samplesHead}</h2>
            </div>
            {RAPPORTVAKT_DELIVERIES_SORTED.map((delivery) => {
              const active = openIds.includes(delivery.id);
              return (
                <div className="rv-accordion__item" key={delivery.id}>
                  <button
                    type="button"
                    className="rv-subrow"
                    aria-expanded={active}
                    onClick={() =>
                      setOpenIds((current) =>
                        current.includes(delivery.id)
                          ? current.filter((id) => id !== delivery.id)
                          : [...current, delivery.id]
                      )
                    }
                  >
                    <PublisherMark publisher={delivery.publisher} />
                    <span className="rv-subrow__text">
                      <span className="rv-subrow__name">{delivery.title}</span>
                      <span className="rv-subrow__meta">{delivery.dateLabel}</span>
                    </span>
                  </button>
                  {active ? <Delivery delivery={delivery} /> : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rv-tabpanel rv-board rv-board--split" id="rv-panel-digest">
            {/* Spans both grid columns (see .rv-board--split > .rv-samples in
                rapportvakt.css), so the label reads across the whole panel
                rather than sitting over just the master list. */}
            <div className="rv-samples">
              <h2 className="rv-samples__head">{t.samplesHead}</h2>
            </div>
            <div className="rv-sublist" role="listbox" aria-label={t.tabDigest}>
              {RAPPORTVAKT_DELIVERIES_SORTED.map((delivery) => {
                const active = delivery.id === selected.id;
                return (
                  <button
                    type="button"
                    className="rv-subrow"
                    role="option"
                    aria-selected={active}
                    key={delivery.id}
                    onClick={() => setSelectedId(delivery.id)}
                  >
                    <PublisherMark publisher={delivery.publisher} />
                    <span className="rv-subrow__text">
                      <span className="rv-subrow__name">{delivery.title}</span>
                      <span className="rv-subrow__meta">{delivery.dateLabel}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <Delivery delivery={selected} />
          </div>
        )
      ) : null}

      {tab === 'bevakningar' ? (
        <div className="rv-tabpanel rv-board" id="rv-panel-bevakningar">
          <div className="rv-catalog">
            <div className="rv-catalog__head">
              {/* First visit: one line saying what to do, above the search. It
                  names the free pick, so a paying reader with nothing watched
                  yet gets the plan-aware line at the bottom instead. */}
              {watches.length === 0 && plan.tier === 'free' ? (
                <p className="rv-catalog__welcome">{t.welcomeLine}</p>
              ) : null}
              <button
                type="button"
                className="rv-btn rv-btn--ghost rv-catalog__addOwnFeed rv-catalog__addOwnFeed--head"
                onClick={openOwnFeedSheet}
                aria-label={t.ownFeedOpen}
              >
                <Glyph name="plus" size={12} />
                <span className="rv-catalog__addOwnFeed-label">{t.ownFeedOpen}</span>
              </button>
            </div>
            {notice === 'offline' ? (
              <p className="rv-catalog__notice" role="status">
                {t.offlineNote}
              </p>
            ) : null}
            <input
              className="rv-input rv-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.searchPlaceholder}
              aria-label={t.searchPlaceholder}
            />
            {/* On a phone the head's own "+" hides (target D): a lonely icon
                next to the welcome sentence had nowhere to go, so under
                560px this full-width ghost button, with its label visible,
                takes over right under the search field instead. Hidden on
                desktop, where the head's button already does the job. */}
            <button
              type="button"
              className="rv-btn rv-btn--ghost rv-catalog__addOwnFeed rv-catalog__addOwnFeed--mobile"
              onClick={openOwnFeedSheet}
            >
              <Glyph name="plus" size={12} />
              <span>{t.ownFeedOpen}</span>
            </button>
            {/* Kind chips (C1/C2): replace the old cadence chips. "Alla"
                first, then only the kinds actually present — an own source
                is always kind 'page', so its chip only appears once the
                reader has added one. */}
            <div className="rv-filters">
              <button
                type="button"
                className="rv-filter"
                aria-pressed={kind === 'all'}
                onClick={() => setKind('all')}
              >
                {t.filterAll}
              </button>
              {availableKinds.map((k) => (
                <button
                  type="button"
                  className="rv-filter"
                  key={k.id}
                  aria-pressed={kind === k.id}
                  onClick={() => setKind(k.id)}
                >
                  {lang === 'en' ? k.labelEn : k.label}
                </button>
              ))}
            </div>
            {sourceRows.length === 0 ? (
              <p className="rv-catalog__intro">{t.noMatches}</p>
            ) : null}
            {/* Own sources and catalogue picks, one list, one row component
                (SourceRow, C1) — own sources sort first (sourceRows is built
                that way in RapportvaktApp). */}
            {sourceRows.map((source) => (
              <SourceRow
                source={source}
                key={source.id}
                watches={watches}
                plan={plan}
                isWatched={!source.isOwn && watches.some((w) => w.seriesId === source.id)}
                signedIn={signedIn}
                knownEmail={cachedEmail}
                confirmedSeriesId={confirmedSeriesId}
                busy={removeBusy}
                ownFeedBusy={ownFeedRemoveBusy}
                onPick={choosePick}
                onLocked={setLockedSeries}
                onRemove={(series) => setRemovingSeriesId(series.id)}
                onRemoveOwn={setRemovingOwnFeed}
              />
            ))}
            <p className="rv-catalog__intro">{catalogIntro}</p>

            {/* Coming soon (not yet implemented, docs/plan/2026-09-13-
                follow-anything-plan.md "Supported sources, v1"): shown so a
                reader sees where the product is going, without pretending
                any of these kinds can be followed yet — the chips are plain
                spans, not filters. */}
            <div className="rv-upcoming">
              <h3 className="rv-upcoming__head">{t.upcomingHead}</h3>
              <div className="rv-upcoming__chips">
                {RAPPORTVAKT_UPCOMING_KINDS.map((k) => (
                  <span className="rv-filter rv-upcoming__chip" key={k.id}>
                    <Glyph name={k.glyph} size={14} />
                    <span>{lang === 'en' ? k.labelEn : k.label}</span>
                  </span>
                ))}
              </div>
              <p className="rv-catalog__intro">
                {t.upcomingNote}
                <button type="button" className="rv-linkbtn" onClick={openOwnFeedSheet}>
                  {t.upcomingRequestLink}
                </button>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'plan' ? (
        <div className="rv-tabpanel rv-plan" id="rv-panel-plan">
          <h2 className="rv-plan__title">{t.planTitle}</h2>

          {notice === 'offline' ? (
            <p className="rv-catalog__notice" role="status">
              {t.offlineNote}
            </p>
          ) : null}

          {/* Signed out: the account control up in the top bar is where a
              reader gets here from, so this is one sentence pointing back at
              it rather than a second sign-in form on the page itself. */}
          {!signedIn ? (
            <div className="rv-plan__status">
              <p>{t.planLoginNudge}</p>
            </div>
          ) : (
            <div className="rv-plan__status">
              {watches.length > 0 ? (
                <>
                  <p>{t.planWatchCountLine(watches.length, accountEmail)}</p>
                  {watches.map((watch) => {
                    const series = RAPPORTVAKT_SERIES.find((s) => s.id === watch.seriesId);
                    return <p key={watch.seriesId}>{series ? series.report : watch.seriesId}</p>;
                  })}
                </>
              ) : (
                <p>
                  {plan.tier === 'free' ? t.planPickBefore : t.planEmptyBefore}
                  <Link to={rapportvaktTabPath('bevakningar')}>{t.tabWatches}</Link>
                  {plan.tier === 'free' ? t.planPickAfter : t.planEmptyAfter}
                </p>
              )}
            </div>
          )}
          <PlanTable currentTier={plan.tier} />
        </div>
      ) : null}
    </main>
  );
}
